<template>
  <div class="editPrompt flex flex-col mt-4 mb-6 max-w-md">
    <div class="flex justify-between mb-4">
      <h1 class="text-left text-lg font-semibold text-primary-500 dark:text-primary-100">
        {{ headerText }}
      </h1>
    </div>
    <div
      v-if="editField === 'name'"
      class="w-full"
    >
      <TextInput
        input-label="Username"
        v-model="editValue"
        @update:model-value="selectValue"
        @submit="submit"
        name="notsearch_user_name"
        autocomplete="new-username"
        outlined
        autofocus
      />
      <!-- Hidden dummy fields -->
      <div class="absolute h-0 overflow-hidden">
        Username <input type="text" name="fake_safari_username">
      </div>
    </div>
    <div
      v-if="editField === 'hourly_rate'"
      class="w-full"
    >
      <NumberInput
        v-model="editValue"
        @update:model-value="selectValue"
        @keydown-enter="submit"
        input-label="Hourly Rate"
        placeholder="$0.00 / hr"
        min="0"
        step="0.01"
        append=" / hr"
        is-dollar outlined
        autofocus
        class="w-full"
      />
    </div>
    <div
      v-if="editField === 'permissions_set_id'"
      class="w-full"
    >
      <VDropdown
        :sel-option="editValue"
        :options="accessiblePermissionSets"
        :disabled="selectedHiddenSet && !hasAccessToHiddenPermissionSets"
        class="w-full"
        input-label="Permission Set"
        placeholder="Select permission set"
        @select="selectPermissionSet"
      />
    </div>
    <!-- POSSIBLE VALUES TO CHOOSE FROM -->
    <ul class="flex flex-wrap gap-1 mt-2 mb-6">
      <li v-for="value of possibleValues" :key="value">
        <button
          @click="selectValue(value)"
          class="rounded text-xs p-0.5 text-primary-700 dark:text-primary-50 font-medium bg-primary-5 hover:bg-primary-15 dark:bg-primarydark/30 dark:hover:bg-primarydark-400/50 dark:hover:text-white border border-gray-300 dark:border-primarydark/50 dark:shadow-primarydark/30 dark:hover:border-primary-50/30"
        >
          {{ displayPossibleValue(value) }}
          <font-awesome-icon
            v-if="editField === 'permissions_set_id' && getHiddenPermissionSetsObj[value]"
            icon="fa-solid fa-shield-halved"
            class="h-3 w-3 mx-0.5"
          />
        </button>
      </li>
    </ul>
    <!-- HIDDEN PERMISSION SET WARNING -->
    <div v-auto-animate="{ duration: 200 }">
      <div v-if="selectedHiddenSet" class="w-full lg:w-96 flex items-start text-sm text-left font-medium text-gray-500 dark:text-gray-400 pb-8">
        <font-awesome-icon icon="fa-solid fa-circle-exclamation" class="h-4 w-4 mt-1 mr-1.5" />
        <p>
          This is a <span class="italic">hidden</span> permission set. This will grant this user <span class="italic">advanced</span> privileges.
        </p>
      </div>
    </div>
    <!-- APPLY TO REGION DEFAULT CHECKBOX -->
    <div
      v-if="hasRegionToApply && anySubscriptions" 
      class="w-full flex-col max-h-80 overflow-y-auto rounded-lg bg-purple-300/10 border border-purple-700/10 dark:bg-purple-900/10 dark:border-gray-700 shadow-sm flex items-center p-2 mb-6 text-sm font-medium"
    >
      <div class="w-full flex items-center">
        <VToggle
          :toggle-bool="isSubscriptionDefault"
          color="purple"
          size="sm"
          @switch-toggle="setSubscriptionDefault"
          :disabled="editing1RegionLeaf || editingMultipleRegionLeaves"
          class="mr-2"
        />
        <p class="text-sm text-purple-900 dark:text-purple-100 font-medium mr-2">
          Region Subscription Default
        </p>
      </div>
      <ul class="flex w-full flex-col gap-2 mt-4 mb-2" v-auto-animate="{ duration: 100 }">
        <!-- REGION STARTING POINTS -->
        <li
          v-for="regionId of Object.keys(regionStartPoints).filter(regionId => !!subscriptions[regionId])"
          :key="regionId"
          class="flex flex-row w-full items-center gap-1.5 ml-6"
        >
          <input
            type="checkbox"
            v-model="checkedRegions[regionId]"
            @change="setCheckedRegions($event, regionId, true)"
            class="h-4 w-4 rounded border-gray-300 dark:border-gray-800 "
            :class="isSubscriptionDefault ? 'cursor-pointer text-purple-600 text-opacity-75 focus:ring-purple-600' : 'text-gray-400 dark:text-gray-300'"
          >
          <p class="font-medium text-gray-600 dark:text-gray-300 text-xs">
            {{ getRegionsObj[regionId].region_name }}
          </p>
        </li>
        <!-- PARENT REGIONS -->
        <li
          v-if="parentRegionsToCheck.length > 0"
          class="flex flex-row w-full items-center gap-1.5 ml-6"
        >
          <input
            type="checkbox"
            ref="parentCheckbox"
            :checked="checkedAllParents"
            @change="setRegionGroup($event, 'allParents')"
            class="h-4 w-4 rounded border-gray-300 dark:border-gray-800 "
            :class="isSubscriptionDefault ? 'cursor-pointer text-purple-600 text-opacity-75 focus:ring-purple-600' : 'text-gray-400 dark:text-gray-300'"
          >
          <button @click="parentsExpanded = !parentsExpanded" class="flex items-center">
            <p class="font-medium text-gray-600 dark:text-gray-300 text-xs mr-1.5">
              Parent regions
            </p>
            <font-awesome-icon icon="fa-solid fa-chevron-down" class="w-3 h-3 text-gray-500 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-100 transform transition-all" :class="parentsExpanded && 'rotate-180'" />
          </button>
        </li>
        <div v-if="parentsExpanded" class="ml-4 my-1 flex w-full flex-col gap-2">
          <li
            v-for="regionId of parentRegionsToCheck"
            :key="`parent-${regionId}`"
            class="flex flex-row w-full items-center gap-1.5 ml-6"
          >
            <input
              type="checkbox"
              v-model="checkedRegions[regionId]"
              @change="setCheckedRegions($event, regionId)"
              class="h-4 w-4 rounded border-gray-300 dark:border-gray-800 "
              :class="isSubscriptionDefault ? 'cursor-pointer text-purple-600 text-opacity-75 focus:ring-purple-600' : 'text-gray-400 dark:text-gray-300'"
            >
            <p class="font-medium text-gray-600 dark:text-gray-300 text-xs">
              {{ getRegionsObj[regionId].region_name }}
            </p>
          </li>
        </div>
        <!-- CHILD REGIONS -->
        <li
          v-if="childRegionsToCheck.length > 0"
          class="flex flex-row w-full items-center gap-1.5 ml-6"
        >
          <input
            type="checkbox"
            :checked="checkedAllChildren"
            ref="childCheckbox"
            @change="setRegionGroup($event, 'allChildren')"
            class="h-4 w-4 rounded border-gray-300 dark:border-gray-800 "
            :class="isSubscriptionDefault ? 'cursor-pointer text-purple-600 text-opacity-75 focus:ring-purple-600' : 'text-gray-400 dark:text-gray-300'"
          >
          <button @click="childrenExpanded = !childrenExpanded" class="flex items-center">
            <p class="font-medium text-gray-600 dark:text-gray-300 text-xs mr-1.5">
              Child regions
            </p>
            <font-awesome-icon icon="fa-solid fa-chevron-down" class="w-3 h-3 text-gray-500 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-100 transform transition-all" :class="childrenExpanded && 'rotate-180'" />
          </button>
        </li>
        <div v-if="childrenExpanded" class="ml-4 my-1 flex w-full flex-col gap-2">
          <li
            v-for="regionID of childRegionsToCheck"
            :key="`child-${regionID}`"
            class="flex flex-row w-full items-center gap-1.5 ml-6"
          >
            <input
              type="checkbox"
              v-model="checkedRegions[regionID]"
              @change="setCheckedRegions($event, regionID)"

              class="h-4 w-4 rounded border-gray-300 dark:border-gray-800 "
              :class="isSubscriptionDefault ? 'cursor-pointer text-purple-600 text-opacity-75 focus:ring-purple-600' : 'text-gray-400'"
            >
            <p class="font-medium text-gray-600 dark:text-gray-300 text-xs">
              {{ getRegionsObj[regionID].region_name }}
            </p>
          </li>
        </div>
      </ul>
    </div>
    <section class="w-full max-w-md flex flex-wrap gap-2 mt-2" :class="siteEmployees.length > 1 && 'sm:flex-nowrap'">
      <VButton
        label="Cancel"
        @click="closePrompt"
        size="sm"
        uppercase
        color="secondary"
        outlined
        class="w-full"
        :class="siteEmployees.length > 1 && 'sm:w-1/2'"
      />
      <VButton
        v-if="siteEmployees.length < 2 ? (siteEmployees[0].permissions_set_id == 'noPermission' ? sitesWithoutPermissions.length > 1 : sitesWithoutPermissions.length > 0 ) : false"
        :label="`${canDoFullSave ? 'Save for' :'Apply to' } Un-Permissioned`"
        @click="applyToNoPermissions"
        size="sm"
        uppercase
        color="info"
        outlined
        :icon="(!submittingNoPermissions) ? 'fa-check-double' : null"
        icon-first
        :disabled="submittingNoPermissions || submitting || submittingAll"
        :submitting-prepend="submittingNoPermissions"
        class="w-full"
      />
      <div class="w-full flex flex-wrap sm:flex-nowrap gap-2" :class="siteEmployees.length > 1 && 'sm:w-1/2'">
        <VButton
          v-if="siteEmployees.length === 1"
          :label="canDoFullSave ? 'Save for All' : 'Apply To All'"
          @click="applyToAll"
          size="sm"
          uppercase
          color="secondary"
          :icon="(!submittingAll) ? 'fa-check-double' : null"
          icon-first
          :disabled="submittingAll || submitting || submittingNoPermissions"
          :submitting-prepend="submittingAll"
          class="w-full sm:w-1/2"
        />
        <VButton
          :label="submitting ? 'Saving...' : (canDoFullSave ? 'Save' : 'Done')"
          @click="submit"
          size="sm"
          uppercase
          color="primary"
          :icon="(canDoFullSave && !submitting) ? 'fa-solid fa-floppy-disk' : null"
          icon-first
          :disabled="submitting || submittingAll || submittingNoPermissions"
          :submitting-prepend="submitting"
          class="w-full"
          :class="siteEmployees.length === 1 && 'sm:w-1/2'"
        />
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from "vuex"
import TextInput from "@/components/Inputs/TextInput/TextInput.vue"
import NumberInput from "@/components/Inputs/NumberInput/NumberInput.vue"
import VDropdown from "@/components/Inputs/DropDown/VDropdown.vue"
import VButton from "@/components/Buttons/Button/VButton.vue"
import VToggle from "@/components/Inputs/Toggle/VToggle.vue"

export default {
  components: {
    TextInput,
    NumberInput,
    VDropdown,
    VButton,
    VToggle
  },
  data() {
    return {
      editValue: null,
      submitting: false,
      submittingAll: false,
      submittingNoPermissions: false,
      selectedHiddenSet: false,
      isSubscriptionDefault: false,
      checkedRegions: {},
      checkedRegionStarts: {},
      parentsExpanded: false,
      childrenExpanded: false,
    }
  },
  computed: {
    ...mapGetters([
      "getProceedArgs",
      "getAllPermissionSets",
      "getPermissionSetsObj",
      "getNonHiddenPermissionSets",
      "getHiddenPermissionSets",
      "getHiddenPermissionSetsObj",
      "getEmployeesSitesEdited",
      "getRegionsEdited",
      "getSiteToRegion",
      "getRegionsObj",
      "getIsTenantSuperuser",
      "getIsAdmin"
    ]),
    siteEmployees() {
      return this.getProceedArgs?.siteEmployees
    },
    editField() {
      return this.getProceedArgs?.editField
    },
    hasAccessToHiddenPermissionSets() {
      return this.getIsTenantSuperuser || this.getIsAdmin
    },
    accessiblePermissionSets() {
      if (this.hasAccessToHiddenPermissionSets) return [ ...this.getHiddenPermissionSets, ...this.getNonHiddenPermissionSets ]
      // if (this.selectedHiddenSet) return [ this.editValue, ...this.getNonHiddenPermissionSets ]
      return this.getNonHiddenPermissionSets
    },
    trueSites() {
      // all actual sites, excluding __noLeaves (regions)
      return this.siteEmployees?.filter((site) => !site.__noLeaves)
    },
    regionLeaves() {
      // all regions, excluding actual sites
      return this.siteEmployees?.filter((site) => site.__noLeaves)
    },
    editing1Site() {
      return this.trueSites.length === 1 && this.regionLeaves.length === 0
    },
    editing1SiteUnderSubscription() {
      return this.editing1Site && this.regionSubscriptionIDs.length > 0
    },
    editingMultipleSites() {
      return this.trueSites.length > 1 && this.regionLeaves.length === 0
    },
    editingMultipleSitesUnderNoSubscription() {
      return this.editingMultipleSites && !this.anySubscriptions
    },
    editingMultipleSitesUnderSameSubscription() {
      return this.editingMultipleSites && this.fromSameRegion
    },
    editingMultipleSitesUnderDifferentSubscriptions() {
      return this.editingMultipleSites && this.anySubscriptions && !this.fromSameRegion
    },
    editing1RegionLeaf() {
      return this.trueSites.length === 0 && this.regionLeaves.length > 0
    },
    editingMultipleRegionLeaves() {
      return this.trueSites.length === 0 && this.regionLeaves.length > 1
    },
    editingSiteAndRegionLeaves() {
      return this.trueSites.length > 0 && this.regionLeaves.length > 0
    },
    hasRegionToApply() {
      return this.editing1SiteUnderSubscription
        || this.editingMultipleSitesUnderSameSubscription
        || this.editingMultipleSitesUnderDifferentSubscriptions
        || this.editing1RegionLeaf
        || this.editingMultipleRegionLeaves
        || this.editingSiteAndRegionLeaves
    },
    regionWithoutSites() {
      return this.siteEmployees.length === 1 && this.siteEmployees[0].__noLeaves
    },
    headerText() {
      const numSites = this.trueSites.length
      return numSites > 1
        ? `Edit ${ numSites} site employees`
        : this.regionWithoutSites
          ? `Edit defaults for ${ this.siteEmployees[0]?.region?.region_name } region` 
          : `Edit site employee at ${ this.firstSite?.name }` 
    },
    firstSite() {
      if (this.regionWithoutSites) return this.subscriptions[this.siteEmployees[0].region?.id]
      return this.trueSites[0]?.site_employee_site_object
    },
    userId() {
      return this.getProceedArgs?.userId
    },
    allSiteEmployees() {
      return this.getProceedArgs?.allSiteEmployees
    },
    subscriptions() {
      return this.getProceedArgs?.subscriptions
    },
    flatSiteEmployees() {
      return Object.values(this.allSiteEmployees).flat()
    },
    allValues() {
      return this.flatSiteEmployees
      .map((employee) => employee[this.editField])
      .filter((value) => {
        if (value == null || value === "noPermission") return false
        // Check hidden permission sets condition
        const isHiddenPermissionSet = 
          this.editField === "permissions_set_id" &&
          !this.hasAccessToHiddenPermissionSets &&
          this.getHiddenPermissionSetsObj[value]

        return !isHiddenPermissionSet
      })
    },
    siteValues() {
      if (this.regionWithoutSites) {
        if (this.editField === "permissions_set_id") {
          return [ this.firstSite?.default_site_permissions_set_id ]
        }
        if (this.editField === "name") {
          return [ this.firstSite?.default_username ]
        }
        if (this.editField === "hourly_rate") {
          return [ this.firstSite?.default_site_hourly_rate ]
        }
      }
      return this.trueSites.map((employee) => employee[this.editField])
    },
    areAllEqual() {
      // Check if all `siteEmployees[editField]` values are equal
      return this.siteValues.every((value) => value === this.siteValues[0])
    },
    possibleValues() {
      if (this.areAllEqual) {
        // If all values are equal, return other values in `allSiteEmployees` ordered by frequency
        return [...new Set(this.allValues)]
          .filter((value) => !this.siteValues.includes(value))
          .sort((a, b) => this.allValues.filter((v) => v === b).length - this.allValues.filter((v) => v === a).length)
      } else {
        // If not all values are equal, return all values ordered as specified
        const siteSet = new Set(this.siteValues)

        return [...new Set(this.allValues)].sort((a, b) => {
          const aInSite = siteSet.has(a) ? 0 : 1
          const bInSite = siteSet.has(b) ? 0 : 1

          if (aInSite !== bInSite) {
            return aInSite - bInSite // Prioritize values in `siteEmployees`
          }

          // Order by frequency if in the same group
          return (
            this.allValues.filter((v) => v === b).length - this.allValues.filter((v) => v === a).length
          )
        })
      }
    },
    sitesWithoutPermissions() {
      return this.allSiteEmployees.noPermission || []
    },
    isEditingAll() {
      if (this.regionWithoutSites) return false
      return this.trueSites.length === this.flatSiteEmployees.length
    },
    canDoFullSave() {
      return false
      // if (!this.userId) return false
      // if (this.regionWithoutSites) return false
      // // should not be able to submit fully if any site employee is missing permission set
      // if (this.sitesWithoutPermissions.length) {
      //   // UNLESS batch editing permission set for all
      //   if (this.editField === "permissions_set_id" && this.isEditingAll) return true
      //   return false
      // }
      // return true
    },
    regionAndParentRegionsIds() {
      let arr = []
      for (let regionLeaf of this.regionLeaves) {
        let levels = [ regionLeaf.region?.id ]
        let current = regionLeaf.region
        while (current.parent_region_id) {
          levels.unshift(current.parent_region_id)
          current = this.getRegionsObj[current.parent_region_id]
        }
        arr.push(levels)
      }
      for (let site of this.trueSites) {
        let levels = this.getSiteToRegion[site.site_id]
        if (!levels) continue
        arr.push(levels)
      }
      return arr
    },
    anySubscriptions() {
      if (this.regionAndParentRegionsIds.length === 0) return false
      if (this.regionSubscriptionIDs.length > 0) return true
      const regionIdArrays = this.regionAndParentRegionsIds
      if (!Array.isArray(regionIdArrays) || regionIdArrays.length === 0) {
        return false
      }
      for (const group of regionIdArrays) {
        for (const regionId of group) {
          if (this.subscriptions[regionId]) {
            return true
          }
        }
      }
      return false

      // return this.subscriptionsOverlap.length > 0
    },
    // underSameSubscribedRegion() {
    //   if (this.regionLeaves.length > 0) return false
    //   for (let )
    // },
    subscriptionsOverlap() {
      const regionIdArrays = this.regionAndParentRegionsIds

      if (!Array.isArray(regionIdArrays) || regionIdArrays.length === 0) {
        return []
      }

      // Start with a Set of the first array
      const intersection = new Set(regionIdArrays[0])

      // Intersect with the remaining arrays
      for (let i = 1; i < regionIdArrays.length; i++) {
        const currentSet = new Set(regionIdArrays[i])
        for (const id of intersection) {
          if (!currentSet.has(id)) {
            intersection.delete(id)
          }
        }
      }

      // Filter only those with existing subscriptions
      return Array.from(intersection).filter(id => !!this.subscriptions[id])
    },
    fromSameRegion() {
      if (this.siteEmployees.length < 2) return true
      let noDuplicates = new Set(this.regionAndParentRegionsIds)
      if (noDuplicates.size === 1) return true
      return false
    },
    regionSubscriptionIDs() {
      if (!this.fromSameRegion) return []
      // if (this.editField === "permissions_set_id") return []
      return this.regionAndParentRegionsIds[0].filter((regionID) => !!this.subscriptions[regionID]).reverse()
    },
    regionStartPoints() {
      return this.getRegionsEdited
    },
    subscribedParentRegionIds() {
      if (!this.anySubscriptions) return {}
      let subscribedParents = {}
      for (let startRegionID of Object.keys(this.regionStartPoints)) {
        subscribedParents[startRegionID] = {}
        let current = this.getRegionsObj[startRegionID]
        while (current.parent_region_id) {
          if (this.subscriptions[current.parent_region_id]) subscribedParents[startRegionID][current.parent_region_id] = true
          current = this.getRegionsObj[current.parent_region_id]
        }
      }
      return subscribedParents
    },
    subscribedChildRegionsIds() {
      if (!this.anySubscriptions) return {}

      const subscribedChildren = {}

      const collectSubscribedRegions = (region, startRegionID) => {
        if (!region?.id) return

        // Add parent region if subscribed
        if (region.id === startRegionID) subscribedChildren[region.id] = {}
        else if (this.subscriptions[region.id]) {
          subscribedChildren[startRegionID][region.id] = true
        }

        if (Array.isArray(region.child_regions)) {
          for (const subregion of region.child_regions) {
            collectSubscribedRegions(subregion, startRegionID)
          }
        }

      }

      for (let employee of this.siteEmployees) {
        if (employee.region && this.regionStartPoints[employee.region.id]) collectSubscribedRegions(employee.region, employee.region.id)
      }

      return subscribedChildren
    },
    childRegionsToCheck() {
      if (!this.anySubscriptions) return []
      let toCheck = {}
      if (Object.keys(this.regionStartPoints).length === 1) return Object.keys(Object.values(this.subscribedChildRegionsIds)[0])
      else {
        for (let startRegionID of Object.keys(this.subscribedChildRegionsIds)) {
          if (this.checkedRegionStarts[startRegionID]) {
            toCheck = { ...toCheck, ...this.subscribedChildRegionsIds[startRegionID] }
          }
        }
      }
      return Object.keys(toCheck)
    },
    parentRegionsToCheck() {
      if (!this.anySubscriptions) return []
      let toCheck = {}
      if (Object.keys(this.regionStartPoints).length === 1) return Object.keys(Object.values(this.subscribedParentRegionIds)[0])
      else {
        for (let startRegionID of Object.keys(this.subscribedParentRegionIds)) {
          if (this.checkedRegionStarts[startRegionID]) {
            toCheck = { ...toCheck, ...this.subscribedParentRegionIds[startRegionID] }
          }
        }
      }
      return Object.keys(toCheck)
    },
    checkedAllParents() {
      for (let regionID of this.parentRegionsToCheck) {
        if (!this.checkedRegions[regionID]) return false
      }
      return true
    },
    checkedSomeParents() {
      for (let regionID of this.parentRegionsToCheck) {
        if (this.checkedRegions[regionID]) return true
      }
      return false
    },
    checkedAllChildren() {
      for (let regionID of this.childRegionsToCheck) {
        if (!this.checkedRegions[regionID]) {
          return false
        }
      }
      return true
    },
    checkedSomeChildren() {
      for (let regionID of this.childRegionsToCheck) {
        if (this.checkedRegions[regionID]) return true
      }
      return false
    },
  },
  async mounted() {
    this.setInitialValue()
    this.setInitialValue()
    this.markInitialRegionsSubs()
    if (this.regionWithoutSites) this.setSubscriptionDefault()
    await this.$nextTick()
    this.setCheckboxState()
  },
  updated() {
    this.setCheckboxState()
  },
  methods: {
    ...mapMutations([
      "setProceedArgs",
      "closePrompt",
      "proceedPrompt",
    ]),
    setInitialValue() {
      let firstValue
      if (this.regionWithoutSites) {
        if (this.editField === "permissions_set_id") {
          firstValue = this.firstSite?.default_site_permissions_set_id
        }
        if (this.editField === "name") {
          firstValue = this.firstSite?.default_username
        }
        if (this.editField === "hourly_rate") {
          firstValue = this.firstSite?.default_site_hourly_rate
        }
      }
      else if (this.trueSites.length === 1 || this.areAllEqual) {
        firstValue = this.trueSites[0][this.editField]
      }
      if (this.editField === "permissions_set_id") {
        if (this.getHiddenPermissionSetsObj[firstValue]) {
          if (!this.hasAccessToHiddenPermissionSets) return
          this.selectedHiddenSet = true
        }
        if (firstValue !== "noPermission") this.editValue = { id: firstValue }
      }
      else this.editValue = firstValue
    },
    markInitialRegionsSubs() {
      if (!this.anySubscriptions) return {}
      let valCheck = this.editValue
      let subFieldCheck
      if (this.editField === "permissions_set_id") {
        subFieldCheck = "default_site_permissions_set_id"
        valCheck = this.editValue?.id
      }
      if (this.editField === "name") {
        subFieldCheck = "default_username"
      }
      if (this.editField === "hourly_rate") {
        subFieldCheck = "default_site_hourly_rate"
      }
      if (valCheck == null) return
      for (let regionID of Object.keys(this.subscriptions)) {
        if (this.subscriptions[regionID][subFieldCheck] == valCheck) {
          this.checkedRegions[regionID] = true
          if (this.regionStartPoints[regionID]) this.checkedRegionStarts[regionID] = true
          this.isSubscriptionDefault = true
        }
      }
    },
    async applyToAll() {
      if (this.editValue === null) {
        this.closePrompt()
        return
      }
      this.setProceedArgs({
        ...this.getProceedArgs,
        appliedToAll: true,
        editValue: (this.editField === "permissions_set_id") ? this.editValue.id : this.editValue
      })
      await this.$nextTick()
      this.submittingAll = true
      this.proceedPrompt()
    },
    async applyToNoPermissions() {
      if (this.editValue === null) {
        this.closePrompt()
        return
      }
      this.setProceedArgs({
        ...this.getProceedArgs,
        siteEmployees: this.allSiteEmployees.noPermission.map((item, idx) => ({
          ...item,
          idx
        })),
        editValue: (this.editField === "permissions_set_id") ? this.editValue.id : this.editValue
      })
      await this.$nextTick()
      this.submittingNoPermissions = true
      this.proceedPrompt()
    },
    setSubscriptionDefault() {
      this.isSubscriptionDefault = !this.isSubscriptionDefault
      this.checkedRegions = {}
      if (this.isSubscriptionDefault) {
        // only check first region if opened prompt directly on region without sites
        if (this.regionWithoutSites) this.checkedRegions[this.siteEmployees[0].region.id] = true
        // else check all regions if opened
        else {
          for (let regionID of Object.keys(this.regionStartPoints)) {
            this.checkedRegions[regionID] = true
            this.checkedRegionStarts[regionID] = true
          }
        }
      }
      this.setProceedArgs({
        ...this.getProceedArgs,
        regionSubscriptionDefaults: this.isSubscriptionDefault ? Object.keys(this.checkedRegions).filter(regionID => !!this.checkedRegions[regionID]) : null
      })
    },
    setCheckedRegions(event, regionID, isStartRegion) {
      if (!this.isSubscriptionDefault) this.isSubscriptionDefault = true
      this.checkedRegions[regionID] = event.target.checked
      if (isStartRegion) this.checkedRegionStarts[regionID] = event.target.checked
      this.setProceedArgs({
        ...this.getProceedArgs,
        regionSubscriptionDefaults: Object.keys(this.checkedRegions).filter(regionID => !!this.checkedRegions[regionID])
      })
    },
    setRegionGroup(event, group) {
      if (!this.isSubscriptionDefault) this.isSubscriptionDefault = true
      if (group == "allParents") {
        for (let regionID of this.parentRegionsToCheck) {
          // do not set to false if region ID is also a start point
          if (!event.target.checked && this.regionStartPoints[regionID]) continue
          this.checkedRegions[regionID] = event.target.checked
        }
      }
      if (group == "allChildren") {
        for (let regionID of this.childRegionsToCheck) {
          // do not set to false if region ID is also a start point
          if (!event.target.checked && this.regionStartPoints[regionID]) continue
          this.checkedRegions[regionID] = event.target.checked
        }
      }
      this.setProceedArgs({
        ...this.getProceedArgs,
        regionSubscriptionDefaults: Object.keys(this.checkedRegions).filter(regionID => !!this.checkedRegions[regionID])
      })
    },
    submit() {
      if (this.editValue === null) {
        this.closePrompt()
        return
      }
      this.submitting = true
      this.proceedPrompt()
    },
    selectPermissionSet(option) {
      this.editValue = option
      this.setProceedArgs({
        ...this.getProceedArgs,
        editValue: option.id
      })
      if (this.getHiddenPermissionSetsObj[option.id]) {
        this.selectedHiddenSet = true
      }
      else this.selectedHiddenSet = false
    },
    selectValue(value) {
      if (this.editField === "permissions_set_id") this.editValue = { id: value }
      else this.editValue = value
      this.setProceedArgs({
        ...this.getProceedArgs,
        editValue: value
      })
      if (this.getHiddenPermissionSetsObj[value]) {
        this.selectedHiddenSet = true
      }
      else this.selectedHiddenSet = false
    },
    displayPossibleValue(value) {
      if (this.editField === "hourly_rate") return `$${value} / hr`
      if (this.editField === "permissions_set_id") return this.getPermissionSetsObj[value]?.name
      else return value
    },
    setCheckboxState() {
      if (this.$refs.parentCheckbox) {
        this.$refs.parentCheckbox.indeterminate = this.checkedSomeParents && !this.checkedAllParents
      }
      if (this.$refs.childCheckbox) {
        this.$refs.childCheckbox.indeterminate = this.checkedSomeChildren && !this.checkedAllChildren
      }
    },
  },
}
</script>

<style scoped>
.editPrompt {
  min-width: 18rem;
}
table {
  border-collapse: collapse;
  background: #fff;
  width: 100%;
  text-align: left;
}
tr {
  border: 1px solid var(--gray-light);
}
thead {
  background: var(--gray-light);
}
:deep(th),
:deep(td) {
  border: 1px solid var(--gray-light);
  padding: 7px;
}

</style>